import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, getUploadUrl } from '../config/api';
import { useInView } from '../hooks/useInView';
import { useAuth } from '../context/AuthContext';

type Banner = { _id: string; image: string };
type Product = {
  _id: string;
  name: string;
  title?: string;
  image?: string;
  images?: string[];
  amountAndWeight?: string;
  price?: number;
  discountedPrice?: number;
  categoryName?: string;
};
type Category = { _id: string; name: string; image?: string };
type SiteSettings = {
  backgroundImage: string | null;
  address: string;
  email: string;
  phone: string;
};

const STEPS = [
  { step: '1', title: 'Create your free account', desc: 'Sign up in under a minute. No payment required.' },
  { step: '2', title: 'Log in anytime', desc: 'Access the catalog from any device, whenever you need it.' },
  { step: '3', title: 'See prices & browse', desc: 'View member-only pricing and filter by category.' },
];

export function Home() {
  const { isAuthenticated } = useAuth();
  const [welcomeBg, setWelcomeBg] = useState<string | null>(null);
  const [siteContact, setSiteContact] = useState<{ address: string; email: string; phone: string } | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [sellerProducts, setSellerProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryRecentProducts, setCategoryRecentProducts] = useState<Record<string, Product[]>>({});
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [loadingSellers, setLoadingSellers] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [bannerPaused, setBannerPaused] = useState(false);
  const [bannerTransition, setBannerTransition] = useState(true);
  const bannerResetRef = useRef(false);
  const bannerCount = banners.length;
  // Duplicate so we can show 3 at once and loop; for 1 banner use 3 copies; for 2 add one more so we can show 2,3,0
  const duplicatedBanners =
    bannerCount === 1
      ? [banners[0], banners[0], banners[0]]
      : bannerCount === 2
        ? [...banners, ...banners, banners[0]]
        : [...banners, ...banners];
  const totalBannerSlides = duplicatedBanners.length;

  const s3 = useInView<HTMLDivElement>();
  const s4 = useInView<HTMLDivElement>();
  // Trigger as soon as the section enters viewport (avoid "white" before 50%).
  const s7 = useInView<HTMLDivElement>({ threshold: 0, rootMargin: '0px 0px -10% 0px' });
  const s9 = useInView<HTMLDivElement>();

  useEffect(() => {
    const loadWelcomeBg = async () => {
      try {
        const res = await axios.get(API.siteSettings.all);
        if (res.data?.success) {
          const data: SiteSettings = res.data?.data || {};
          setWelcomeBg(data.backgroundImage || null);
          setSiteContact({
            address: data.address || '',
            email: data.email || '',
            phone: data.phone || '',
          });
        }
      } catch {
        setWelcomeBg(null);
        setSiteContact(null);
      }
    };
    loadWelcomeBg();
  }, []);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const res = await axios.get(API.banners);
        if (res.data.success) setBanners(res.data.data || []);
      } catch {
        setBanners([]);
      } finally {
        setLoadingBanners(false);
      }
    };
    loadBanners();
  }, []);

  useEffect(() => {
    if (bannerCount > 0 && bannerIndex >= bannerCount) setBannerIndex(0);
  }, [bannerCount, bannerIndex]);

  // Auto-play: show next 3 (infinite)
  useEffect(() => {
    if (bannerCount < 2 || bannerPaused) return;
    const id = setInterval(() => {
      setBannerIndex((i) => (i + 1) % bannerCount);
    }, 5000);
    return () => clearInterval(id);
  }, [bannerCount, bannerPaused]);

  // Infinite loop: when we animated to duplicated position (same as 0), reset without transition
  const handleBannerTransitionEnd = () => {
    if (!bannerResetRef.current) return;
    bannerResetRef.current = false;
    setBannerTransition(false);
    setBannerIndex(0);
    requestAnimationFrame(() => requestAnimationFrame(() => setBannerTransition(true)));
  };

  const goToNext = () => {
    if (bannerCount <= 1) return;
    if (bannerIndex === bannerCount - 1) {
      bannerResetRef.current = true;
      setBannerTransition(true);
      setBannerIndex(bannerCount);
    } else {
      setBannerIndex((i) => i + 1);
    }
  };

  const goToPrev = () => {
    if (bannerCount <= 1) return;
    if (bannerIndex === 0) {
      setBannerTransition(false);
      setBannerIndex(bannerCount - 1);
      requestAnimationFrame(() => setBannerTransition(true));
    } else {
      setBannerIndex((i) => i - 1);
    }
  };

  useEffect(() => {
    const loadSellers = async () => {
      try {
        const res = await axios.get(API.sellerProducts);
        if (res.data.success) setSellerProducts(res.data.data || []);
      } catch {
        setSellerProducts([]);
      } finally {
        setLoadingSellers(false);
      }
    };
    loadSellers();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await axios.get(API.categories);
        if (res.data.success) setCategories(res.data.data || []);
      } catch {
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Load last 4 products per category for the shop-by-category section
  useEffect(() => {
    if (categories.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const results: Record<string, Product[]> = {};
      await Promise.all(
        categories.map(async (c) => {
          try {
            const res = await axios.get(API.productsRecentByCategory(c._id));
            if (!cancelled && res.data?.success) results[c._id] = res.data.data || [];
          } catch {
            if (!cancelled) results[c._id] = [];
          }
        })
      );
      if (!cancelled) setCategoryRecentProducts(results);
    };
    load();
    return () => { cancelled = true; };
  }, [categories]);

  const reveal = (ref: { inView: boolean }) => (ref.inView ? 'reveal in-view' : 'reveal');

  return (
    <div className="bg-slate-50/30">
      {/* ——— Welcome banner: background (admin-managed), text left, product image right ——— */}
      <section
        className="relative w-full min-h-[320px] sm:min-h-[380px] md:min-h-[420px] lg:min-h-[480px] flex items-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${welcomeBg ? getUploadUrl(welcomeBg) : '/images/bg-welcome.jpg'})` }}
      >
        {/* Subtle overlay so the background texture stays visible */}
        <div className="absolute inset-0 bg-slate-900/40" aria-hidden />
        <div className="container mx-auto px-6 sm:px-8 py-10 sm:py-14 relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-12">
          {/* Left: text and buttons */}
          <div className="max-w-xl lg:max-w-2xl flex-shrink-0">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              Welcome to AG Cash & Carry
            </h1>
            <p className="mt-3 sm:mt-4 text-xl sm:text-2xl md:text-3xl font-bold text-white/95 leading-snug">
              Your trusted catering partner bringing service, value and convenience!
            </p>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-white/90 font-normal max-w-lg leading-relaxed">
              Quality ingredients, sauces, packaging and drinks for takeaways, restaurants, coffee shops and catering businesses.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg font-semibold text-slate-900 bg-amber-400 hover:bg-amber-300 transition-colors shadow-lg"
              >
                Browse Products
              </Link>
              <Link
                to="/products?category=weekly-deals"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg font-semibold text-slate-900 bg-amber-400 hover:bg-amber-300 transition-colors shadow-lg"
              >
                Weekly Deals
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* ——— Banner carousel (full width) ——— */}
      <section className="relative overflow-hidden py-4 sm:py-6 w-full">
        <div className="w-full">
          {loadingBanners ? (
            <div className="w-full aspect-[21/9] max-h-64 sm:max-h-72 md:max-h-80 bg-slate-800/50 flex items-center justify-center text-slate-400 text-sm font-medium">Loading…</div>
          ) : banners.length === 0 ? (
            <div className="w-full aspect-[21/9] max-h-64 sm:max-h-72 md:max-h-80 bg-slate-800/50 flex items-center justify-center text-slate-400 text-sm font-medium">No banners yet</div>
          ) : (
              <div
                className="relative overflow-hidden w-full aspect-[21/9] max-h-64 sm:max-h-72 md:max-h-80"
                onMouseEnter={() => setBannerPaused(true)}
                onMouseLeave={() => setBannerPaused(false)}
              >
                {/* Show 3 banners at once; duplicate for infinite loop */}
                <div
                  className="flex h-full"
                  style={{
                    width: `${(totalBannerSlides * 100) / 3}%`,
                    transform: `translateX(-${(bannerIndex * 100) / totalBannerSlides}%)`,
                    transition: bannerTransition ? 'transform 700ms cubic-bezier(0.22,1,0.36,1)' : 'none',
                  }}
                  onTransitionEnd={handleBannerTransitionEnd}
                >
                  {duplicatedBanners.map((b, i) => (
                    <div
                      key={`${b._id}-${i}`}
                      className="flex-shrink-0 h-full px-1.5 sm:px-2 md:px-3"
                      style={{ width: `${100 / totalBannerSlides}%` }}
                    >
                      <div className="w-full h-full rounded-lg overflow-hidden">
                        <img
                          src={getUploadUrl(b.image)}
                          alt=""
                          className="w-full h-full object-cover object-center"
                          loading={i < 3 ? 'eager' : 'lazy'}
                          decoding="async"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {bannerCount > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goToPrev}
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center text-slate-800 hover:bg-white hover:scale-105 transition-all duration-200 z-10"
                      aria-label="Previous banner"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={goToNext}
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center text-slate-800 hover:bg-white hover:scale-105 transition-all duration-200 z-10"
                      aria-label="Next banner"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
                      {banners.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setBannerIndex(i)}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            i === bannerIndex ? 'w-8 bg-amber-400' : 'w-1.5 bg-white/60 hover:bg-white/80'
                          }`}
                          aria-label={`Go to banner ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
        </div>
      </section>

      {/* ——— Top Sellers (horizontal auto-scroll, infinite) ——— */}
      <section className="bg-white">
        <div className="px-6 sm:px-8 py-8">
          <div ref={s9.ref} className={`container mx-auto flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14 ${reveal(s9)}`}>
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary tracking-tight">
                Top Sellers
              </h2>
            </div>
            <p className="text-slate-500 text-sm sm:text-base font-medium">Log in to see prices</p>
          </div>
          {loadingSellers ? (
            <div className="container mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-2xl bg-slate-100 h-72 animate-pulse" />
              ))}
            </div>
          ) : sellerProducts.length === 0 ? (
            <p className="text-slate-500 text-center py-20 font-medium">No seller products yet.</p>
          ) : (
            <div className="overflow-hidden seller-scroll-wrap">
              <div
                className="flex gap-5 sm:gap-6 animate-seller-scroll"
                style={{ width: 'max-content' }}
              >
                {[...sellerProducts, ...sellerProducts].map((p, index) => (
                  <div
                    key={`${p._id}-${index}`}
                    className="card-hover group flex-shrink-0 w-[280px] sm:w-[320px] rounded-2xl border border-slate-200/90 bg-white overflow-hidden shadow-sm"
                  >
                    <div className="aspect-[4/3] bg-slate-100 w-full overflow-hidden transition-transform duration-500 ease-out group-hover:scale-[1.04]">
                      {(p.images?.length ? p.images[0] : p.image) ? (
                        <img
                          src={getUploadUrl(p.images?.length ? p.images[0] : p.image)}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">No image</div>
                      )}
                    </div>
                    <div className="p-4 sm:p-5">
                      <p className="font-bold text-primary truncate">{p.name}</p>
                      {p.amountAndWeight ? <p className="text-xs text-slate-400 mt-1">{p.amountAndWeight}</p> : null}
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        {isAuthenticated && p.price != null ? (
                          <p className="text-amber-600 font-bold">£{Number(p.price).toFixed(2)}</p>
                        ) : (
                          <p className="text-slate-400 text-xs">Log in to see price</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ——— Shop by category (ref on section so observer always runs and content reveals) ——— */}
      <section ref={s7.ref} className="bg-white border-y border-slate-200/80">
        <div className="container mx-auto px-6 sm:px-8 py-8">
          <p className={`section-label text-center mb-3 ${reveal(s7)}`}>Browse by category</p>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-primary tracking-tight text-center mb-14 ${reveal(s7)}`}>
            Shop by category
          </h2>
          {loadingCategories ? (
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                  <div className="h-56 sm:h-72 bg-slate-200 animate-pulse" />
                  <div className="grid grid-cols-4 gap-px bg-slate-200">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="h-32 bg-slate-100 animate-pulse" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`space-y-8 sm:space-y-10 stagger-children ${s7.inView ? 'in-view' : ''}`}>
              {categories.map((c) => {
                const recentProducts = categoryRecentProducts[c._id] || [];
                return (
                  <div
                    key={c._id}
                    className="rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Link
                      to={`/products?category=${encodeURIComponent(c._id)}`}
                      className="block relative min-h-[200px] sm:min-h-[280px] md:min-h-[420px]"
                    >
                      <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                          backgroundImage: c.image
                            ? `url(${getUploadUrl(c.image)})`
                            : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                        }}
                        aria-hidden
                      />
                      <div className="absolute inset-0 bg-slate-900/40" aria-hidden />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg text-center px-4">
                          {c.name}
                        </h3>
                      </div>
                    </Link>
                    {recentProducts.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 border-t border-slate-200 pt-6">
                        {recentProducts.map((p) => (
                          <Link
                            key={p._id}
                            to={`/products/${p._id}`}
                            className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col hover:shadow-lg hover:border-slate-300 transition-all duration-200"
                          >
                            {/* Image section with "New" overlay */}
                            <div className="relative aspect-[4/3] w-full bg-slate-100 overflow-hidden flex-shrink-0">
                              {(p.images?.length ? p.images[0] : p.image) ? (
                                <img
                                  src={getUploadUrl(p.images?.length ? p.images[0] : p.image)}
                                  alt={p.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                                  No image
                                </div>
                              )}
                              <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-pink-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                                New
                              </span>
                            </div>
                            {/* Footer: price, name, details (white background) */}
                            <div className="p-3 sm:p-4 flex-1 flex flex-col min-w-0 bg-white">
                              <div className="flex items-start justify-between gap-2">
                                {isAuthenticated ? (
                                  p.discountedPrice != null && Number(p.discountedPrice) > 0 ? (
                                    <div className="flex flex-wrap items-baseline gap-1.5">
                                      <p className="text-slate-900 font-bold text-base sm:text-lg">£{Number(p.discountedPrice).toFixed(2)}</p>
                                      {p.price != null && (
                                        <p className="text-slate-400 text-sm line-through">£{Number(p.price).toFixed(2)}</p>
                                      )}
                                    </div>
                                  ) : p.price != null ? (
                                    <p className="text-slate-900 font-bold text-base sm:text-lg">£{Number(p.price).toFixed(2)}</p>
                                  ) : (
                                    <p className="text-slate-400 text-xs font-medium">Log in to see price</p>
                                  )
                                ) : (
                                  <p className="text-slate-400 text-xs font-medium">Log in to see price</p>
                                )}
                                <span className="flex-shrink-0 text-slate-300 hover:text-slate-500 transition-colors" aria-hidden>
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                  </svg>
                                </span>
                              </div>
                              <p className="font-medium text-slate-800 text-sm sm:text-base mt-1 line-clamp-2">{p.name}</p>
                              {p.amountAndWeight && (
                                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-slate-500 text-xs sm:text-sm">
                                  <span className="flex items-center gap-0.5">
                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8 4-8-4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    {p.amountAndWeight}
                                  </span>
                                </div>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section> 

      {/* ——— Hero (search directly below banner, then headline) ——— */}
      <section className="relative min-h-[520px] sm:min-h-[600px] flex flex-col overflow-hidden bg-primary-dark">
        <div className="absolute inset-0 bg-primary" aria-hidden />
        <div className="absolute inset-0 hero-gradient" aria-hidden />
        <div className="absolute inset-0 hero-glow" aria-hidden />
        <div className="absolute inset-0 bg-grid-pattern opacity-40" aria-hidden />
        <div className="absolute top-0 left-0 right-0 h-px bg-white/10" aria-hidden />

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-8 text-center flex-1 flex flex-col justify-center pt-8 pb-16 sm:pb-28">
          <h1 className="hero-line text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-white leading-[1.08]">
            Quality products.
            <br />
            <span className="text-amber-400 drop-shadow-sm">Competitive prices.</span>
          </h1>
          <p className="hero-line mt-8 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Browse our full catalog and log in to see member-only pricing. No commitment — sign up in seconds.
          </p>
          <div className="hero-line mt-14 flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="hover-lift inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/30"
            >
              Create an account
            </Link>
            <Link
              to="/login"
              className="hover-lift inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 border-white/50 text-white hover:bg-white/15 transition-colors backdrop-blur-sm"
            >
              Log in
            </Link>
          </div>
        </div>
      </section>

      {/* ——— How it works (3 steps) ——— */}
      <section className="bg-white border-y border-slate-200/80">
        <div className="container mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p ref={s3.ref} className={`section-label text-center mb-3 ${reveal(s3)}`}>How it works</p>
          <h2 ref={s3.ref} className={`text-center text-3xl sm:text-4xl lg:text-5xl font-bold text-primary tracking-tight mb-20 ${reveal(s3)}`}>
            Three steps to member pricing
          </h2>
          <div
            ref={s3.ref}
            className={`grid md:grid-cols-3 gap-10 lg:gap-14 max-w-5xl mx-auto stagger-children ${s3.inView ? 'in-view' : ''}`}
          >
            {STEPS.map((item, i) => (
              <div key={i} className="relative text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 text-white font-bold text-xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/30 ring-4 ring-amber-500/20">
                  {item.step}
                </div>
                <h3 className="mt-8 font-bold text-primary text-lg sm:text-xl">{item.title}</h3>
                <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">{item.desc}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] h-px bg-gradient-to-r from-slate-200 to-slate-200" aria-hidden />
                )}
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <Link
              to="/register"
              className="hover-lift inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold bg-primary text-white hover:bg-primary-light transition-colors shadow-lg shadow-slate-900/10"
            >
              Start with step 1 — Sign up free
            </Link>
          </div>
        </div>
      </section>

      {/* ——— Why choose us ——— */}
      <section className="bg-white border-y border-slate-200/80">
        <div className="container mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <div
            ref={s4.ref}
            className={`max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center ${reveal(s4)}`}
          >
            <div>
              <p className="section-label mb-4">Why choose us</p>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-primary tracking-tight leading-tight">
                Built for trade. Priced for you.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed text-lg">
                We supply a wide range of products at competitive trade prices. Create an account to access
                up-to-date pricing and browse our full catalog by category — no commitment, no hassle.
              </p>
              <ul className="mt-10 space-y-5">
                {[
                  'Fresh product list updated regularly',
                  'Clear categories for easy browsing',
                  'Member-only pricing for your business',
                  'No commitment — sign up and browse anytime',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center mt-0.5">
                      <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-slate-700 font-medium pt-1">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200/80 min-h-[320px]">
              <img src="/images/image1.png" alt="" className="w-full h-full object-cover object-center min-h-[320px]" />
            </div>
          </div>
        </div>
      </section>

      {/* ——— Map / Location section ——— */}
      <section className="bg-slate-50 border-b border-slate-200/80">
        <div className="container mx-auto px-6 sm:px-8 py-12 sm:py-16">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8 items-stretch">
            <div className="lg:col-span-1 flex flex-col justify-center">
              <p className="section-label mb-3">Find us</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-primary tracking-tight mb-4">
                Visit AG Cash &amp; Carry
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4">
                {siteContact?.address
                  ? `${siteContact.address}. Conveniently located with easy access and parking for trade customers.`
                  : 'Conveniently located with easy access and parking for trade customers.'}
              </p>
              <p className="text-slate-600 text-sm sm:text-base">
                Use the map to get directions and plan your visit.
              </p>
            </div>
            <div className="lg:col-span-2 h-72 sm:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-sm ring-1 ring-slate-200 bg-slate-200">
              <iframe
                title="AG Cash & Carry location"
                src="https://www.google.com/maps?q=53.56528,-0.0789&z=16&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ——— Footer ——— */}
      <footer className="bg-primary border-t border-slate-700/50">
        <div className="container mx-auto px-6 sm:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
            <div className="lg:col-span-5">
              <p className="text-xl font-semibold tracking-tight text-white">AG Cash & Carry</p>
              <p className="mt-1 text-slate-400 text-sm">Quality products, competitive prices.</p>
            </div>
            <nav className="flex flex-wrap gap-6 sm:gap-8 lg:col-span-7 lg:justify-end lg:items-start">
              <Link to="/" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">
                Home
              </Link>
              <Link to="/products" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">
                Products
              </Link>
              <Link to="/about-us" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">
                About Us
              </Link>
              <Link to="/login" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">
                Login
              </Link>
              <Link to="/register" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">
                Sign up
              </Link>
            </nav>
          </div>
          {(siteContact?.address || siteContact?.email || siteContact?.phone) && (
            <div className="flex flex-row flex-wrap justify-between text-slate-400 text-sm mt-4 gap-4">
              {siteContact?.address ? (
                <p className="flex items-start gap-3">
                  <span className="text-amber-400/80 shrink-0">Address:</span>
                  <span>{siteContact.address}</span>
                </p>
              ) : null}
              {siteContact?.email ? (
                <p className="flex items-start gap-3">
                  <span className="text-amber-400/80 shrink-0">Email:</span>
                  <a
                    href={`mailto:${siteContact.email}`}
                    className="hover:text-white transition-colors"
                  >
                    {siteContact.email}
                  </a>
                </p>
              ) : null}
              {siteContact?.phone ? (
                <p className="flex items-start gap-3">
                  <span className="text-amber-400/80 shrink-0">Phone:</span>
                  <a
                    href={`tel:${siteContact.phone.replace(/\s/g, '')}`}
                    className="hover:text-white transition-colors"
                  >
                    {siteContact.phone}
                  </a>
                </p>
              ) : null}
            </div>
          )}
          <div className="mt-10 pt-8 border-t border-slate-700/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Cash & Carry. All rights reserved.</p>
            <p className="text-slate-500 text-sm">Grimsby, UK</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
